import { EllipsisVerticalIcon } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { useCopyPaste } from '@/hooks/useCopyPaste';
import useOperatingSystem from '@/hooks/useOperatingSystem';
import { cn } from '@/lib/utils';
import { TYPES, iconByType, isFinalAction } from '@/redux/dialogs/helper';
import { isModalOpen } from '@/redux/modals/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CopiedObject } from '@/util/CopyPasteController';
import { preventClickThrough } from '@/util/events';

import { ActionMenu, menuTriggerClassName } from '../ActionMenu/ActionMenu';

import styles from './ActionHeader.module.scss';

type Props = {
  type: string;
  onDelete?: () => void;
  children?: React.ReactNode;
  objectToCopy: CopiedObject;
  isSelected: boolean;
  hasDialogError?: boolean;
};

const ActionHeader = ({
  type,
  children,
  onDelete,
  objectToCopy,
  isSelected,
  hasDialogError = false,
}: Props) => {
  const { handlePaste, handleCopy } = useCopyPaste();
  const { t } = useTranslation();
  const modalOpen = useSelector(isModalOpen);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { deleteShortcut } = useOperatingSystem();

  useHotkeys(
    'mod+v',
    () => handlePaste(),
    {
      enabled: canWrite && isSelected && !modalOpen && type !== 'reminder',
      enableOnFormTags: false,
    },
    [handlePaste, canWrite, isSelected, modalOpen, type]
  );

  useHotkeys(
    'mod+c',
    () => handleCopy(objectToCopy),
    { enabled: isSelected && !modalOpen && type !== 'reminder', enableOnFormTags: false },
    [handleCopy, objectToCopy, isSelected, modalOpen, type]
  );

  useHotkeys(
    'delete',
    onDelete,
    { enabled: canWrite && isSelected && !modalOpen, enableOnFormTags: false },
    [onDelete, canWrite, isSelected, modalOpen]
  );

  // Override the default header text for some action types
  const getHeader = () => {
    switch (type) {
      case 'text':
        return t(`actions.types.text_message`);
      case 'event':
        return t(`actions.types.jump_to`);
      case 'reminder':
        return t('actions.types.on_inactivity');
      case 'sub_agent':
        return t('actions.types.authentication');
      default:
        return t(`actions.types.${type}`);
    }
  };

  return (
    <div className={cn(styles.header, { [styles.condition]: type === 'condition' })}>
      {!children &&
        iconByType({ id: type, size: 16, color: 'var(--color-foreground)', type: TYPES.ACTION })}
      <p>{children ?? <span className="text-sm leading-5 font-semibold">{getHeader()}</span>}</p>

      {/*
        Stop clicks originating from the menu (the trigger button, the menu
        items, and — crucially — the portaled backdrop) from bubbling up to the
        action box. React propagates synthetic events through the portal's React
        tree, not the DOM tree, so a backdrop/menu click would otherwise reach
        the action box's onClick and select the action / open its side panel,
        even though the menu is rendered in a portal on document.body (MOV-16957).
      */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.header__icons} onClick={preventClickThrough}>
        <ActionMenu
          trigger={
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              className={menuTriggerClassName}
              aria-label="menu-btn"
              aria-controls="card-menu"
              aria-haspopup
            >
              <EllipsisVerticalIcon size={20} />
            </Button>
          }
          handleDelete={onDelete}
          handleCopy={handleCopy}
          handlePaste={() => handlePaste(objectToCopy)}
          objectToCopy={objectToCopy}
          disableCopy={hasDialogError}
          // Override default options for last actions
          options={
            isFinalAction(type)
              ? [
                  {
                    label: t('dialog.actions.menu.delete'),
                    shortcut: deleteShortcut,
                    onClick: onDelete,
                    requiresWrite: true,
                  },
                ]
              : []
          }
        />
      </div>
    </div>
  );
};

export default ActionHeader;
